import { ChevronDown } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Dropdown } from "@themes/pollinator/ui/Dropdown";
import { type MarketingNavItem } from "./marketingNavigationItems.data";
import { User } from "@platform/types/platform.types";
import { PlatformAvatarDropdown } from "@themes/pollinator/ui/PlatformAvatarDropdown";

export type Props = {
  onNavigationClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    to: string
  ) => void;
  onNavigationAnchorClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    hash: string,
    /** The item to keep marked on the way, where it is not the destination's */
    pinId?: string
  ) => void;
  user: User | null;
  showLoginCta: boolean;
  menuItems: { title: string; href: string }[];
  /** The nav's own items, which the container narrows before passing them */
  items: MarketingNavItem[];
  /** The href of the item for where the reader is now, if any */
  activeHref?: string | null;
};

export function MarketingNavigationItems({
  onNavigationClick,
  onNavigationAnchorClick,
  user,
  menuItems,
  items,
  activeHref,
}: Props) {
  // Every item is drawn the same way, whether it stands on its own or is the
  // thing a dropdown opens from.
  const itemButton = (item: MarketingNavItem) => (
    <Button
      key={item.title}
      as="a"
      variant="ghost"
      // The design draws the item for where the reader is as a yellow
      // pill, and the rest with the smaller corner of a ghost button.
      className={`lg:px-1.5 lg:py-1 ${
        item.href === activeHref
          ? "bg-brand-100 border-brand-100 lg:rounded-full"
          : "lg:rounded-lg"
      } ${item.items ? "hover:bg-transparent hover:border-transparent" : ""}`}
      aria-current={item.href === activeHref ? "true" : undefined}
      // A click does not take the focus. Its ring stayed on the item
      // clicked while the yellow mark moved on with the scroll, so the
      // two disagreed; the keyboard still focuses items, with the ring.
      onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
      typeClassName="lg:px-0.5 text-[16px] md:text-[16px] leading-[24px] font-bold"
      data-testid={`marketing-nav-${item.title.toLowerCase()}`}
      href={item.href}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
        // These scroll to a section of the homepage; anything else is a
        // route of its own, and scrolling to a section that is not here
        // does nothing at all.
        item.href.includes("#")
          ? onNavigationAnchorClick(e, item.href.split("#")[1])
          : onNavigationClick(e, item.href)
      }
      title={item.title}
    >
      {item.title}
    </Button>
  );

  return (
    <>
      <nav className="flex flex-col w-full justify-start items-start gap-6 px-4 py-4 lg:flex-row lg:items-center lg:justify-end lg:gap-0.5 lg:py-0 lg:px-0">
        {items.map((item) =>
          item.items ? (
            // An item with sections under it is the item beside a caret, and
            // the two are one trigger: resting on either opens the sections,
            // so the caret says what is there without being the only way to
            // reach it. The word is still the link it always was, carrying
            // its own address and its own scroll.
            <Dropdown
              key={item.title}
              openOnHover
              // The mark for the pointer belongs to the pair, not to whichever
              // half it is over: one border and one background around both,
              // and each half keeps its own only for the focus ring. The
              // radius is the item's own at either width, since this wrapper
              // has none to inherit: the pill of the stacked menu, the
              // smaller corner of the row.
              triggerClassName="flex items-center border border-transparent rounded-full lg:rounded-lg hover:bg-brand-100 hover:border-brand-100"
              trigger={
                <>
                  {itemButton(item)}
                  <Button
                    variant="ghost"
                    className="lg:px-0.5 lg:py-1 lg:rounded-lg hover:bg-transparent hover:border-transparent"
                    iconComponent={ChevronDown}
                    aria-label={`${item.title} sections`}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                    data-testid={`marketing-nav-${item.title.toLowerCase()}-caret`}
                  />
                </>
              }
              data-testid={`marketing-nav-${item.title.toLowerCase()}-dropdown`}
            >
              <div className="flex flex-col py-1">
                {item.items.map((sub) => (
                  <Button
                    key={sub.title}
                    as="a"
                    variant="menu-item"
                    // An inset, so the words sit off the menu's border while
                    // the row itself still fills it for the hover.
                    className="px-4"
                    href={sub.href}
                    data-testid={`marketing-subnav-${sub.title.toLowerCase()}`}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                      onNavigationAnchorClick(e, sub.href.split("#")[1], sub.pinId)
                    }
                    title={sub.title}
                  >
                    {sub.title}
                  </Button>
                ))}
              </div>
            </Dropdown>
          ) : (
            itemButton(item)
          )
        )}
      </nav>

      {user && (
        <div className="py-6 border-t border-brand-800 lg:border-none lg:py-0 lg:shrink-0">
          <div className="flex flex-col items-start gap-3 px-4 w-full lg:flex-row lg:items-center lg:px-0 lg:gap-2">
            <PlatformAvatarDropdown
              photo={user.photo}
              fullName={user.first_name + " " + user.last_name}
              isLoaded={!!user}
              onNavigationClick={onNavigationClick}
              menuItems={menuItems}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default MarketingNavigationItems;

